import { useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import {
  isCustomSpendMatcher,
  type MatchWith,
  type MatcherOperator,
} from "@north-beam/nb-common/breakdown-v2";
import { Select } from "@shared/selects";

import { MATCH_WITH_LABEL, OPERATOR_LABEL } from ".";

const MATCH_WITH_OPTION_LABEL: Record<MatchWith, string> = {
  name: "Name",
  "utm.source": "utm_source",
  "utm.medium": "utm_medium",
  "utm.campaign": "utm_campaign",
  "utm.term": "utm_term",
  "utm.content": "utm_content",
  "custom-spend-sheet": "Spend Sheet/API Name",
  "custom-spend-api-platform": "Spend API Platform",
};

interface Params {
  className?: string;
  matchWith: MatchWith;
  operator: MatcherOperator;
  setMenuRef?: (ref: HTMLDivElement | null) => void;
  onChangeMatchWith: (value: MatchWith) => void;
  onChangeOperator: (value: MatcherOperator) => void;
}

export const OperatorSelector = ({
  className,
  matchWith,
  operator,
  setMenuRef,
  onChangeMatchWith,
  onChangeOperator,
}: Params) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    refs,
    floatingStyles,
    context,
    update: updateFloating,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom-start",
    middleware: [flip(), offset(8)],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => updateFloating(), [matchWith, updateFloating]);

  return (
    <div className={clsx("shrink-0", className)}>
      <button
        className={clsx(
          "text-xs text-cosmic rounded-l border h-8 px-2 flex items-center gap-2",
          "bg-cosmic-5 border-cosmic-1 hover:border-cosmic hover:z-[1] hover:relative",
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div className="text-left text-[11px] leading-[13px]">
          <div className="font-normal text-[9px] leading-[9px]">
            {MATCH_WITH_LABEL[matchWith]}
          </div>
          <div className={clsx("font-bold")}>{OPERATOR_LABEL[operator]}</div>
        </div>
        <i
          className={clsx("fa-regular fa-sharp", {
            "fa-chevron-down": !isOpen,
            "fa-chevron-up": isOpen,
          })}
        ></i>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={(ref) => {
              setMenuRef?.(ref);
              refs.setFloating(ref);
            }}
            style={floatingStyles}
            className={clsx(
              "z-[2005] bg-white rounded-lg elevation-1 text-sm pt-3 pb-2 w-[320px]",
            )}
            {...getFloatingProps()}
          >
            <div className="mx-4 mb-3 pb-3 border-b border-gray-2">
              <h5 className="text-xs tracking-wider mb-3">MATCH WITH</h5>
              <Select
                options={[
                  { label: MATCH_WITH_OPTION_LABEL["name"], value: "name" },
                  {
                    label: MATCH_WITH_OPTION_LABEL["utm.source"],
                    value: "utm.source",
                  },
                  {
                    label: MATCH_WITH_OPTION_LABEL["utm.medium"],
                    value: "utm.medium",
                  },
                  {
                    label: MATCH_WITH_OPTION_LABEL["utm.campaign"],
                    value: "utm.campaign",
                  },
                  {
                    label: MATCH_WITH_OPTION_LABEL["utm.term"],
                    value: "utm.term",
                  },
                  {
                    label: MATCH_WITH_OPTION_LABEL["utm.content"],
                    value: "utm.content",
                  },
                  {
                    label: MATCH_WITH_OPTION_LABEL["custom-spend-sheet"],
                    value: "custom-spend-sheet",
                  },
                  {
                    label: MATCH_WITH_OPTION_LABEL["custom-spend-api-platform"],
                    value: "custom-spend-api-platform",
                  },
                ]}
                value={matchWith}
                onChange={onChangeMatchWith}
              />
              {isCustomSpendMatcher(matchWith) && (
                <p className="text-[9px] mt-1.5 mb-0 leading-3">
                  *does not match with any automatically ingested platform ads
                </p>
              )}
            </div>
            <h5 className="mx-4 mt-4 mb-2 text-xs tracking-wider">LOGIC</h5>
            {(
              Object.entries(OPERATOR_LABEL) as [MatcherOperator, string][]
            ).map(([value, title]) => (
              <div
                key={value}
                className={clsx(
                  "px-4 h-9 w-full flex items-center gap-2",
                  "cursor-pointer text-xs hover:bg-cosmic-4",
                  { "font-bold bg-cosmic-4": value === operator },
                )}
                onClick={() => {
                  setIsOpen(false);
                  onChangeOperator(value);
                }}
              >
                {title}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
